import { supabaseClient } from './supabase';
import { logger } from './logger';
import type { StandardItem, StandardPlace } from '../types/standard';
import { HOTEL_ID } from './supabase/configClient';

class StandardClient {
  private static instance: StandardClient;

  private constructor() {}

  public static getInstance(): StandardClient {
    if (!StandardClient.instance) {
      StandardClient.instance = new StandardClient();
    }
    return StandardClient.instance;
  }

  public async getItems(sectionId: string): Promise<StandardItem[]> {
    try {
      logger.debug('Loading standard items', {
        category: 'API',
        data: { 
          hotel_id: HOTEL_ID,
          section_id: sectionId
        }
      });

      // Vérifier les paramètres requis
      if (!sectionId) {
        throw new Error('Section ID is required');
      }

      // Charger les items, traductions et configuration de la section en parallèle
      const [itemsResponse, translationsResponse, sectionResponse] = await Promise.all([
        supabaseClient
          .from('hotels_section')
          .select('*')
          .eq('hotel_id', HOTEL_ID)
          .eq('section_id', sectionId),
        
        supabaseClient
          .from('translations_section')
          .select('*')
          .eq('hotel_id', HOTEL_ID)
          .eq('section_id', sectionId),

        supabaseClient
          .from('hotels_navigation')
          .select('filter_open_section')
          .eq('id', sectionId)
          .single()
      ]);

      if (itemsResponse.error) {
        throw itemsResponse.error;
      }

      if (translationsResponse.error) {
        throw translationsResponse.error;
      }

      // Créer un map des traductions par item_id
      const translationsMap = new Map();
      translationsResponse.data.forEach(translation => {
        const translations = {
          fr: translation.fr,
          en: translation.en,
          es: translation.es,
          it: translation.it,
          pt: translation.pt,
          de: translation.de,
          nl: translation.nl,
          ja: translation.ja,
          zh: translation.zh,
          ru: translation.ru
        };

        if (translation.item_id) {
          translationsMap.set(translation.item_id, translations);

          logger.debug('Found translations for item', {
            category: 'Translation',
            data: {
              item_id: translation.item_id,
              translations: Object.entries(translations)
                .filter(([_, value]) => value)
                .reduce((acc, [key, value]) => ({
                  ...acc,
                  [key]: value.substring(0, 100) + (value.length > 100 ? '...' : '')
                }), {}),
              availableLanguages: Object.entries(translations)
                .filter(([_, value]) => value)
                .map(([key]) => key),
              section_id: sectionId
            }
          });
        }
      });

      // Log raw data for debugging
      logger.debug('Raw standard items data', {
        category: 'API',
        data: {
          itemCount: itemsResponse.data.length,
          firstItem: itemsResponse.data[0] ? {
            id: itemsResponse.data[0].id,
            name: itemsResponse.data[0].name,
            timewalk: itemsResponse.data[0].timewalk,
            timedrive: itemsResponse.data[0].timedrive,
            timebike: itemsResponse.data[0].timebike
          } : null
        }
      });

      // Transform the data into StandardItems with translations
      const items = itemsResponse.data.map(record => {
        // Récupérer les traductions pour cet item
        const translations = translationsMap.get(record.id) || {};

        logger.debug('Processing item with translations', {
          category: 'Translation',
          data: {
            item_id: record.id,
            item_name: record.name,
            description: record.description?.substring(0, 100) + (record.description?.length > 100 ? '...' : ''),
            has_translations: Object.keys(translations).length > 0,
            translations: Object.entries(translations)
              .filter(([_, value]) => value)
              .reduce((acc, [key, value]) => ({
                ...acc,
                [key]: value.substring(0, 100) + (value.length > 100 ? '...' : '')
              }), {}),
            section_id: sectionId,
            travel_times: {
              timewalk: record.timewalk,
              timedrive: record.timedrive,
              timebike: record.timebike
            }
          }
        });

        return {
          id: record.id,
          name: record.name,
          descriptions: translations,
          description: record.description,
          fulladress: record.fulladress || '',
          telephone: record.telephone || '',
          url: record.url || '',
          category: record.category || 'Autre',
          type: record.type || '',
          image: record.image_metadata?.url || '',
          latitude: record.latitude?.toString() || '0',
          longitude: record.longitude?.toString() || '0',
          distance: record.distance?.toString() || '',
          timewalk: record.timewalk?.toString() || '',
          timedrive: record.timedrive?.toString() || '',
          timebike: record.timebike?.toString() || '',
          booking: record.booking || '',
          price: record.price || '',
          ouverture_lundi: record.ouverture_lundi === true ? 'oui' : 'non',
          inHotel: record.inhotel || false,
          source: 'standard' as const
        };
      });

      // Utiliser le tri initial de la section
      const initialSortType = sectionResponse.data?.filter_open_section;
      if (initialSortType) {
        return this.sortItems(items, initialSortType);
      }

      // Par défaut, trier par nom
      return items.sort((a, b) => a.name.localeCompare(b.name));

    } catch (error) {
      logger.error('Error fetching standard items', {
        category: 'API',
        data: { error, sectionId, hotelId: HOTEL_ID }
      });
      throw error;
    }
  }

  private sortItems(items: StandardItem[], sortType?: string): StandardItem[] {
    switch (sortType) {
      case 'shuffle':
        return [...items].sort(() => Math.random() - 0.5);
      
      case 'name':
        return [...items].sort((a, b) => a.name.localeCompare(b.name));
      
      case 'distance':
        return [...items].sort((a, b) => {
          const distA = parseFloat(a.distance) || 0;
          const distB = parseFloat(b.distance) || 0;
          return distA - distB;
        });
      
      case 'timewalk':
        return [...items].sort((a, b) => {
          const timeA = parseInt(a.timewalk) || 0;
          const timeB = parseInt(b.timewalk) || 0;
          return timeA - timeB;
        });
      
      case 'timedrive':
        return [...items].sort((a, b) => {
          const timeA = parseInt(a.timedrive) || 0;
          const timeB = parseInt(b.timedrive) || 0;
          return timeA - timeB;
        });
      
      case 'category':
        return [...items].sort((a, b) => {
          const catCompare = a.category.localeCompare(b.category);
          if (catCompare !== 0) return catCompare;
          return a.name.localeCompare(b.name);
        });
      
      default:
        return items;
    }
  }

  public getPlaces(items: StandardItem[]): StandardPlace[] {
    try {
      const placeMap = new Map<string, StandardPlace>();

      items.forEach(item => {
        if (!placeMap.has(item.name)) {
          placeMap.set(item.name, {
            name: item.name,
            latitude: item.latitude,
            longitude: item.longitude,
            distance: item.distance,
            timewalk: item.timewalk,
            fulladress: item.fulladress,
            items: []
          });
        }

        const place = placeMap.get(item.name)!;
        place.items.push(item);
      });

      logger.debug('Places created successfully', {
        category: 'API',
        data: { 
          placeCount: placeMap.size,
          places: Array.from(placeMap.values()).map(place => ({
            name: place.name,
            itemCount: place.items.length
          }))
        }
      });

      return Array.from(placeMap.values());

    } catch (error) {
      logger.error('Error creating places', {
        category: 'API',
        data: { error }
      });
      return [];
    }
  }
}

const standardClient = StandardClient.getInstance();

export { standardClient };